import { useParams, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, CheckCircle, Star } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import GetStartedDialog from "@/components/GetStartedDialog";
import CaseStudiesDialog from "@/components/CaseStudiesDialog";
import salesforceImage from "@/assets/1-salesforce-crm.jpg";
import salesforceBg from "@/assets/salesforce-bg.jpg";
import { 
  Target,
  MessageSquare,
  BarChart3,
  Globe,
  Users,
  Cog,
  Database,
  Shield,
  Zap,
  FileText,
  DollarSign,
  Calendar,
  TrendingUp,
  Factory,
  Heart,
  BookOpen,
  Building,
  Truck,
  Settings,
  Award,
  Layers,
  CreditCard,
  UserCheck,
  Briefcase,
  Monitor,
  GitBranch,
  TestTube,
  Rocket,
  Network,
  CheckCircle as Check
} from "lucide-react";

interface SalesforceModuleData {
  // icon: any;
  title: string;
  // category: string;
  description: string;
  features: string[];
  technologies: string[];
  benefits: string[];
  process: string[];
  overview: string;
  implementation: {
    strategy: string[];
    configuration: string[];
    optimization: string[];
  };
  clouds: string[];
}

const salesforceModuleData: Record<string, SalesforceModuleData> = {
  "sales-cloud": {
    // icon: Target,
    title: "Sales Cloud",
    // category: "Sales",
    description: "Lead management, opportunity tracking, and sales forecasting for comprehensive sales automation.",
    overview: "Salesforce Sales Cloud empowers sales teams with comprehensive CRM capabilities, from lead generation to deal closure. It provides 360-degree customer views, automated workflows, and AI-powered insights to drive sales productivity and revenue growth.",
    features: ["Lead Management", "Opportunity Tracking", "Sales Forecasting", "Territory Management", "Quote Generation", "Sales Analytics"],
    technologies: ["Sales Cloud", "Einstein AI", "Salesforce Mobile", "Lightning Platform", "Pardot", "CPQ"],
    benefits: ["Increased Sales Productivity", "Better Forecasting Accuracy", "Improved Lead Conversion", "Enhanced Customer Relationships", "Mobile Access", "AI-Powered Insights"],
    process: ["Sales Process Analysis", "Lead Management Setup", "Opportunity Configuration", "Territory Planning", "Forecasting Setup", "User Training"],
    implementation: {
      strategy: ["Sales Process Mapping", "Territory Design", "Lead Routing Strategy", "Forecasting Framework", "KPI Definition"],
      configuration: ["Lead & Opportunity Setup", "Sales Process Configuration", "Territory Management", "Quote & Pricing", "Reporting & Dashboards"],
      optimization: ["Process Automation", "Einstein AI Configuration", "Mobile Optimization", "Performance Monitoring", "Continuous Improvement"]
    },
    clouds: ["Sales Cloud", "Service Cloud", "Marketing Cloud", "Commerce Cloud", "Analytics Cloud", "Platform"]
  },
  "service-cloud": {
    // icon: MessageSquare,
    title: "Service Cloud",
    // category: "Service",
    description: "Case management, knowledge base, and customer support for exceptional service delivery.",
    overview: "Salesforce Service Cloud transforms customer service operations with intelligent case management, omnichannel support, and self-service capabilities. It enables organizations to deliver personalized, efficient service across all touchpoints.",
    features: ["Case Management", "Knowledge Base", "Omni-Channel Routing", "Field Service", "Live Chat", "Service Analytics"],
    technologies: ["Service Cloud", "Einstein Case Classification", "Live Agent", "Field Service Lightning", "Community Cloud", "CTI"],
    benefits: ["Faster Case Resolution", "Improved Customer Satisfaction", "Reduced Service Costs", "Agent Productivity", "Omnichannel Support", "Self-Service Options"],
    process: ["Service Process Design", "Case Management Setup", "Knowledge Base Creation", "Channel Configuration", "SLA Setup", "Agent Training"],
    implementation: {
      strategy: ["Service Strategy Definition", "Channel Strategy", "SLA Framework", "Knowledge Strategy", "Self-Service Planning"],
      configuration: ["Case Management Setup", "Omni-Channel Configuration", "Knowledge Base Setup", "Service Console", "CTI Integration"],
      optimization: ["Automation Rules", "Einstein Features", "Performance Dashboards", "Quality Monitoring", "Continuous Enhancement"]
    },
    clouds: ["Service Cloud", "Sales Cloud", "Community Cloud", "Field Service", "Einstein Platform", "Mobile"]
  },
  "marketing-cloud": {
    // icon: BarChart3,
    title: "Marketing Cloud",
    // category: "Marketing",
    description: "Email marketing, campaign automation, and customer journeys for personalized marketing.",
    overview: "Salesforce Marketing Cloud delivers personalized customer experiences across all channels with AI-powered insights, journey automation, and real-time engagement. It enables marketers to create meaningful connections at scale.",
    features: ["Email Marketing", "Customer Journeys", "Social Media Marketing", "Mobile Messaging", "Advertising Studio", "Marketing Analytics"],
    technologies: ["Marketing Cloud", "Journey Builder", "Email Studio", "Social Studio", "Mobile Studio", "Einstein"],
    benefits: ["Personalized Experiences", "Improved Engagement", "Better ROI", "Cross-Channel Consistency", "Real-time Insights", "Automated Campaigns"],
    process: ["Marketing Strategy", "Journey Mapping", "Content Creation", "Automation Setup", "Analytics Configuration", "Team Training"],
    implementation: {
      strategy: ["Marketing Strategy Definition", "Customer Journey Mapping", "Content Strategy", "Channel Strategy", "Measurement Framework"],
      configuration: ["Platform Setup", "Journey Builder", "Content Creation", "Automation Rules", "Analytics Setup"],
      optimization: ["A/B Testing", "Einstein Insights", "Performance Optimization", "Personalization", "Advanced Automation"]
    },
    clouds: ["Marketing Cloud", "Sales Cloud", "Service Cloud", "Commerce Cloud", "Data Cloud", "Analytics"]
  },
  "commerce-cloud": {
    // icon: Globe,
    title: "Commerce Cloud",
    // category: "Commerce",
    description: "E-commerce platform, product catalog, and order management for digital commerce.",
    overview: "Salesforce Commerce Cloud provides a comprehensive e-commerce platform that enables businesses to create seamless shopping experiences across all channels. It combines B2C and B2B commerce capabilities with AI-powered personalization.",
    features: ["E-commerce Platform", "Product Catalog", "Order Management", "Payment Processing", "Inventory Management", "Customer Segmentation"],
    technologies: ["Commerce Cloud", "B2C Commerce", "B2B Commerce", "Order Management", "Einstein Commerce", "Mobile SDK"],
    benefits: ["Unified Commerce Experience", "Increased Conversion Rates", "Better Customer Insights", "Scalable Platform", "Mobile Optimization", "AI Personalization"],
    process: ["Commerce Strategy", "Platform Setup", "Catalog Configuration", "Payment Integration", "Testing & Launch", "Performance Optimization"],
    implementation: {
      strategy: ["Commerce Strategy Definition", "Channel Strategy", "Customer Experience Design", "Integration Planning", "Migration Strategy"],
      configuration: ["Platform Configuration", "Catalog Setup", "Payment Gateway Integration", "Shipping Configuration", "Tax Setup"],
      optimization: ["Performance Tuning", "Personalization Setup", "Analytics Implementation", "Mobile Optimization", "Security Enhancement"]
    },
    clouds: ["Commerce Cloud", "Service Cloud", "Marketing Cloud", "Sales Cloud", "Analytics Cloud", "Einstein Platform"]
  },
  "community-cloud": {
    // icon: Users,
    title: "Community Cloud",
    // category: "Community",
    description: "Partner portals, customer communities, and collaboration for community engagement.",
    overview: "Salesforce Community Cloud enables organizations to create branded digital experiences for customers, partners, and employees. It provides self-service capabilities, collaboration tools, and seamless integration with other Salesforce clouds.",
    features: ["Partner Portals", "Customer Communities", "Collaboration Tools", "Content Management", "Self-Service", "Mobile Access"],
    technologies: ["Community Cloud", "Experience Cloud", "Partner Communities", "Customer Communities", "Lightning Components", "Chatter"],
    benefits: ["Enhanced Partner Relationships", "Improved Customer Satisfaction", "Reduced Support Costs", "Better Collaboration", "Self-Service Capabilities", "Brand Consistency"],
    process: ["Community Strategy", "Design & Branding", "Content Creation", "User Management", "Launch & Training", "Community Management"],
    implementation: {
      strategy: ["Community Strategy", "User Experience Design", "Content Strategy", "Engagement Strategy", "Success Metrics"],
      configuration: ["Community Setup", "Branding & Design", "User Permissions", "Content Management", "Integration Setup"],
      optimization: ["User Engagement", "Content Optimization", "Performance Monitoring", "Feature Enhancement", "Community Growth"]
    },
    clouds: ["Community Cloud", "Service Cloud", "Sales Cloud", "Marketing Cloud", "Commerce Cloud", "Chatter"]
  },
  "analytics-cloud": {
    // icon: BarChart3,
    title: "Analytics Cloud",
    // category: "Analytics",
    description: "Business intelligence, data visualization, and predictive analytics for data-driven insights.",
    overview: "Salesforce Analytics Cloud (Tableau CRM) provides powerful business intelligence and analytics capabilities within the Salesforce platform. It offers AI-powered insights, interactive dashboards, and predictive analytics to drive better business decisions.",
    features: ["Business Intelligence", "Data Visualization", "Predictive Analytics", "Mobile Analytics", "Real-time Dashboards", "Einstein Analytics"],
    technologies: ["Tableau CRM", "Einstein Analytics", "Wave Analytics", "Analytics Studio", "Salesforce Connect", "Einstein Discovery"],
    benefits: ["Data-Driven Decisions", "Real-time Insights", "Predictive Intelligence", "Mobile Analytics", "Embedded Analytics", "Self-Service BI"],
    process: ["Analytics Strategy", "Data Assessment", "Dashboard Design", "Model Building", "User Training", "Adoption & Optimization"],
    implementation: {
      strategy: ["Analytics Strategy", "Data Architecture", "KPI Definition", "User Adoption Strategy", "Governance Framework"],
      configuration: ["Data Integration", "Dashboard Creation", "Security Setup", "Mobile Configuration", "Einstein Setup"],
      optimization: ["Performance Tuning", "Advanced Analytics", "Predictive Models", "User Experience", "Continuous Improvement"]
    },
    clouds: ["Analytics Cloud", "Sales Cloud", "Service Cloud", "Marketing Cloud", "Commerce Cloud", "Einstein Platform"]
  },
  "app-cloud": {
    // icon: Cog,
    title: "App Cloud (Platform)",
    // category: "Platform",
    description: "Custom app development, workflow automation, and integration for platform solutions.",
    overview: "Salesforce App Cloud provides a comprehensive platform for building custom applications, automating business processes, and integrating systems. It enables rapid development with declarative tools and programmatic capabilities.",
    features: ["Custom App Development", "Workflow Automation", "Process Builder", "Lightning Components", "APIs", "Integration Tools"],
    technologies: ["Lightning Platform", "Apex", "Visualforce", "Lightning Web Components", "Platform Events", "Salesforce APIs"],
    benefits: ["Rapid Development", "Scalable Applications", "Process Automation", "System Integration", "Mobile-First Design", "Enterprise Security"],
    process: ["Requirements Analysis", "Application Design", "Development", "Testing", "Deployment", "Maintenance"],
    implementation: {
      strategy: ["Platform Strategy", "Architecture Design", "Development Approach", "Integration Strategy", "Deployment Planning"],
      configuration: ["Platform Setup", "Custom Objects", "Workflow Rules", "Security Configuration", "Integration Setup"],
      optimization: ["Performance Optimization", "Code Quality", "User Experience", "Security Enhancement", "Scalability Planning"]
    },
    clouds: ["App Cloud", "Sales Cloud", "Service Cloud", "Marketing Cloud", "Commerce Cloud", "Analytics Cloud"]
  },
  "data-cloud": {
    // icon: Database,
    title: "Data Cloud",
    // category: "Data",
    description: "Customer data platform, data integration, and identity resolution for unified data management.",
    overview: "Salesforce Data Cloud provides a comprehensive customer data platform that unifies data from multiple sources, creates a single view of the customer, and enables real-time personalization across all touchpoints.",
    features: ["Customer Data Platform", "Data Integration", "Identity Resolution", "Real-time Data", "Data Harmonization", "Privacy Management"],
    technologies: ["Salesforce CDP", "Data Integration", "Customer 360", "Real-time APIs", "MuleSoft", "Tableau"],
    benefits: ["Unified Customer View", "Real-time Insights", "Better Personalization", "Data Governance", "Privacy Compliance", "Improved ROI"],
    process: ["Data Strategy", "Data Assessment", "Integration Design", "Identity Resolution", "Testing & Validation", "Activation"],
    implementation: {
      strategy: ["Data Strategy", "Source System Analysis", "Identity Resolution Strategy", "Privacy Framework", "Activation Planning"],
      configuration: ["Data Source Integration", "Identity Resolution", "Data Model Setup", "Real-time Streaming", "Security Configuration"],
      optimization: ["Data Quality", "Performance Tuning", "Advanced Segmentation", "Predictive Modeling", "Continuous Enhancement"]
    },
    clouds: ["Data Cloud", "Marketing Cloud", "Commerce Cloud", "Service Cloud", "Analytics Cloud", "Einstein Platform"]
  },
  "security": {
    // icon: Shield,
    title: "Security & Identity",
    // category: "Security",
    description: "Single sign-on, multi-factor authentication, and encryption for comprehensive security.",
    overview: "Salesforce Security & Identity provides comprehensive security solutions including identity management, access control, encryption, and compliance tools to protect your organization's data and ensure regulatory compliance.",
    features: ["Single Sign-On", "Multi-factor Authentication", "Encryption", "Identity Management", "Access Control", "Compliance Tools"],
    technologies: ["Salesforce Identity", "Shield Platform Encryption", "Event Monitoring", "Security Center", "Single Sign-On", "Multi-Factor Authentication"],
    benefits: ["Enhanced Security", "Compliance Assurance", "User Experience", "Risk Reduction", "Audit Capabilities", "Identity Management"],
    process: ["Security Assessment", "Identity Strategy", "Implementation Planning", "Configuration", "Testing", "Monitoring"],
    implementation: {
      strategy: ["Security Strategy", "Identity Architecture", "Compliance Requirements", "Risk Assessment", "Governance Framework"],
      configuration: ["Identity Setup", "Authentication Methods", "Encryption Configuration", "Access Controls", "Monitoring Setup"],
      optimization: ["Security Hardening", "Performance Optimization", "User Experience", "Compliance Monitoring", "Threat Detection"]
    },
    clouds: ["Identity Cloud", "Sales Cloud", "Service Cloud", "Marketing Cloud", "Commerce Cloud", "Platform"]
  },
  "integration": {
    // icon: Zap,
    title: "Integration Cloud",
    // category: "Integration",
    description: "API management, data synchronization, and process automation for seamless integration.",
    overview: "Salesforce Integration Cloud, powered by MuleSoft, provides comprehensive integration capabilities to connect systems, data, and processes across your organization, enabling seamless digital transformation.",
    features: ["API Management", "Data Synchronization", "Process Automation", "Real-time Integration", "Legacy System Integration", "Cloud Connectivity"],
    technologies: ["MuleSoft", "Salesforce Integration", "Platform Events", "Streaming API", "REST/SOAP APIs", "ETL Tools"],
    benefits: ["System Connectivity", "Data Consistency", "Process Automation", "Real-time Updates", "Legacy Modernization", "Scalable Architecture"],
    process: ["Integration Strategy", "System Analysis", "Design & Architecture", "Development", "Testing", "Deployment"],
    implementation: {
      strategy: ["Integration Strategy", "System Architecture", "Data Flow Design", "Security Strategy", "Performance Planning"],
      configuration: ["API Setup", "Data Mapping", "Process Flows", "Security Configuration", "Monitoring Setup"],
      optimization: ["Performance Tuning", "Error Handling", "Scalability Enhancement", "Security Hardening", "Monitoring Optimization"]
    },
    clouds: ["Integration Cloud", "Sales Cloud", "Service Cloud", "Marketing Cloud", "Commerce Cloud", "Data Cloud"]
  },
  "documents": {
    // icon: FileText,
    title: "Document Generation",
    // category: "Documents",
    description: "Contract creation, proposal automation, and document templates for document management.",
    overview: "Salesforce Document Generation enables automated creation of professional documents, contracts, and proposals directly from Salesforce data. It streamlines document workflows and ensures consistency across all business communications.",
    features: ["Contract Creation", "Proposal Automation", "Document Templates", "E-signature Integration", "Version Control", "Approval Workflows"],
    technologies: ["Salesforce Documents", "DocuSign", "Document Generation", "Template Builder", "PDF Generation", "E-signature APIs"],
    benefits: ["Faster Document Creation", "Consistency", "Reduced Errors", "Streamlined Workflows", "Legal Compliance", "Time Savings"],
    process: ["Template Design", "Data Mapping", "Workflow Setup", "Integration Configuration", "Testing", "User Training"],
    implementation: {
      strategy: ["Document Strategy", "Template Standards", "Workflow Design", "Integration Planning", "Compliance Framework"],
      configuration: ["Template Setup", "Data Field Mapping", "Approval Workflows", "E-signature Integration", "Version Control"],
      optimization: ["Template Optimization", "Workflow Automation", "Performance Tuning", "User Experience", "Compliance Monitoring"]
    },
    clouds: ["Sales Cloud", "Service Cloud", "Legal", "CPQ", "Contract Management", "DocuSign"]
  },
  "cpq": {
    // icon: DollarSign,
    title: "CPQ (Configure Price Quote)",
    // category: "Quoting",
    description: "Product configuration, pricing rules, and quote generation for sales automation.",
    overview: "Salesforce CPQ streamlines the quote-to-cash process with intelligent product configuration, dynamic pricing, and automated quote generation. It helps sales teams create accurate quotes faster and close deals more efficiently.",
    features: ["Product Configuration", "Pricing Rules", "Quote Generation", "Approval Workflows", "Contract Management", "Billing Integration"],
    technologies: ["Salesforce CPQ", "Product Configurator", "Pricing Engine", "Quote Templates", "Approval Framework", "Revenue Cloud"],
    benefits: ["Faster Quote Generation", "Pricing Accuracy", "Reduced Errors", "Improved Margins", "Sales Efficiency", "Revenue Growth"],
    process: ["Product Catalog Setup", "Pricing Configuration", "Quote Templates", "Approval Rules", "Integration Setup", "User Training"],
    implementation: {
      strategy: ["CPQ Strategy", "Product Catalog Design", "Pricing Strategy", "Approval Framework", "Integration Planning"],
      configuration: ["Product Setup", "Pricing Rules", "Quote Templates", "Approval Workflows", "Integration Configuration"],
      optimization: ["Pricing Optimization", "Quote Automation", "Performance Tuning", "User Experience", "Revenue Analytics"]
    },
    clouds: ["CPQ", "Sales Cloud", "Revenue Cloud", "Commerce Cloud", "Billing", "Contract Management"]
  },
  "scheduling": {
    // icon: Calendar,
    title: "Scheduling",
    // category: "Scheduling",
    description: "Appointment booking, resource management, and calendar integration for scheduling optimization.",
    overview: "Salesforce Scheduler provides intelligent appointment booking and resource management capabilities. It enables businesses to optimize scheduling processes, reduce no-shows, and improve customer experience through automated scheduling workflows.",
    features: ["Appointment Booking", "Resource Management", "Calendar Integration", "Automated Scheduling", "Availability Management", "Reminder Notifications"],
    technologies: ["Salesforce Scheduler", "Lightning Scheduler", "Resource Calendar", "Booking APIs", "Calendar Integration", "SMS/Email Notifications"],
    benefits: ["Improved Scheduling Efficiency", "Reduced No-shows", "Better Resource Utilization", "Enhanced Customer Experience", "Automated Workflows", "Real-time Availability"],
    process: ["Scheduling Strategy", "Resource Setup", "Calendar Configuration", "Booking Rules", "Integration Setup", "User Training"],
    implementation: {
      strategy: ["Scheduling Strategy", "Resource Planning", "Availability Rules", "Customer Experience Design", "Integration Strategy"],
      configuration: ["Resource Setup", "Calendar Integration", "Booking Rules", "Notification Setup", "User Permissions"],
      optimization: ["Scheduling Optimization", "Automation Rules", "Performance Monitoring", "User Experience", "Analytics Setup"]
    },
    clouds: ["Scheduler", "Service Cloud", "Sales Cloud", "Field Service", "Marketing Cloud", "Experience Cloud"]
  },
  "revenue-cloud": {
    // icon: TrendingUp,
    title: "Revenue Cloud",
    // category: "Revenue",
    description: "Subscription management, billing automation, and revenue recognition for revenue operations.",
    overview: "Salesforce Revenue Cloud provides end-to-end revenue lifecycle management from quote to cash. It includes subscription management, automated billing, and revenue recognition to help businesses optimize their revenue operations and ensure compliance.",
    features: ["Subscription Management", "Billing Automation", "Revenue Recognition", "Contract Management", "Usage-based Billing", "Revenue Analytics"],
    technologies: ["Revenue Cloud", "Billing", "Subscription Management", "Revenue Recognition", "Contract Lifecycle", "Financial Reporting"],
    benefits: ["Automated Revenue Operations", "Improved Cash Flow", "Compliance Assurance", "Scalable Billing", "Revenue Insights", "Subscription Growth"],
    process: ["Revenue Strategy", "Billing Setup", "Contract Configuration", "Revenue Rules", "Integration Setup", "Financial Reporting"],
    implementation: {
      strategy: ["Revenue Strategy", "Billing Model Design", "Contract Framework", "Compliance Requirements", "Integration Planning"],
      configuration: ["Billing Setup", "Subscription Products", "Revenue Rules", "Contract Templates", "Financial Integration"],
      optimization: ["Revenue Optimization", "Billing Automation", "Performance Analytics", "Compliance Monitoring", "Customer Experience"]
    },
    clouds: ["Revenue Cloud", "CPQ", "Billing", "Sales Cloud", "Service Cloud", "Analytics Cloud"]
  },
  "manufacturing": {
    // icon: Factory,
    title: "Manufacturing Cloud",
    // category: "Manufacturing",
    description: "Account planning, sales agreements, and partner management for manufacturing industry.",
    overview: "Salesforce Manufacturing Cloud provides industry-specific capabilities for manufacturers including account-based forecasting, sales agreements, and partner relationship management. It helps manufacturers build stronger relationships and drive predictable revenue growth.",
    features: ["Account Planning", "Sales Agreements", "Partner Management", "Forecast Management", "Rebate Management", "Channel Management"],
    technologies: ["Manufacturing Cloud", "Sales Agreements", "Account Forecasts", "Partner Portal", "Rebate Management", "Channel Analytics"],
    benefits: ["Predictable Revenue", "Stronger Partnerships", "Better Forecasting", "Streamlined Operations", "Improved Margins", "Enhanced Collaboration"],
    process: ["Manufacturing Strategy", "Account Planning", "Partner Onboarding", "Sales Agreement Setup", "Forecast Configuration", "Channel Management"],
    implementation: {
      strategy: ["Manufacturing Strategy", "Account Segmentation", "Partner Strategy", "Forecast Framework", "Channel Design"],
      configuration: ["Account Setup", "Sales Agreements", "Partner Portal", "Forecast Configuration", "Rebate Rules"],
      optimization: ["Account Optimization", "Partner Engagement", "Forecast Accuracy", "Channel Performance", "Revenue Growth"]
    },
    clouds: ["Manufacturing Cloud", "Sales Cloud", "Partner Communities", "CPQ", "Analytics Cloud", "Revenue Cloud"]
  },
  "health-cloud": {
    // icon: Heart,
    title: "Health Cloud",
    // category: "Healthcare",
    description: "Patient management, care coordination, and provider networks for healthcare industry.",
    overview: "Salesforce Health Cloud transforms healthcare delivery with comprehensive patient relationship management, care coordination, and provider collaboration tools. It enables healthcare organizations to deliver personalized, connected care experiences.",
    features: ["Patient Management", "Care Coordination", "Provider Networks", "Health Records", "Care Plans", "Patient Engagement"],
    technologies: ["Health Cloud", "Patient Management", "Care Plans", "Provider Directory", "Health Records", "HIPAA Compliance"],
    benefits: ["Improved Patient Outcomes", "Better Care Coordination", "Enhanced Provider Collaboration", "Streamlined Operations", "Regulatory Compliance", "Patient Satisfaction"],
    process: ["Healthcare Strategy", "Patient Data Integration", "Care Team Setup", "Provider Network", "Compliance Configuration", "Staff Training"],
    implementation: {
      strategy: ["Healthcare Strategy", "Patient Experience Design", "Care Coordination Framework", "Provider Network Strategy", "Compliance Planning"],
      configuration: ["Patient Records Setup", "Care Team Configuration", "Provider Directory", "Compliance Settings", "Integration Setup"],
      optimization: ["Care Optimization", "Provider Engagement", "Patient Experience", "Compliance Monitoring", "Outcome Analytics"]
    },
    clouds: ["Health Cloud", "Service Cloud", "Marketing Cloud", "Community Cloud", "Analytics Cloud", "Einstein Platform"]
  },
  "education-cloud": {
    // icon: BookOpen,
    title: "Education Cloud",
    // category: "Education",
    description: "Student recruitment, academic advising, and alumni relations for education industry.",
    overview: "Salesforce Education Cloud empowers educational institutions to manage the entire student lifecycle from recruitment to alumni engagement. It provides tools for student success, academic advising, and institutional advancement.",
    features: ["Student Recruitment", "Academic Advising", "Alumni Relations", "Student Success", "Institutional Advancement", "Program Management"],
    technologies: ["Education Cloud", "Student Success Hub", "Advisor Link", "Alumni Management", "Program Management", "Student Analytics"],
    benefits: ["Improved Student Outcomes", "Enhanced Recruitment", "Better Retention", "Alumni Engagement", "Operational Efficiency", "Data-Driven Decisions"],
    process: ["Education Strategy", "Student Journey Mapping", "Academic Setup", "Alumni Program", "Analytics Configuration", "Staff Training"],
    implementation: {
      strategy: ["Education Strategy", "Student Experience Design", "Academic Framework", "Alumni Strategy", "Success Metrics"],
      configuration: ["Student Records", "Academic Programs", "Advisor Setup", "Alumni Database", "Analytics Dashboards"],
      optimization: ["Student Success", "Recruitment Optimization", "Retention Strategies", "Alumni Engagement", "Institutional Growth"]
    },
    clouds: ["Education Cloud", "Marketing Cloud", "Service Cloud", "Community Cloud", "Analytics Cloud", "Nonprofit Cloud"]
  },
  "financial-services": {
    // icon: Building,
    title: "Financial Services Cloud",
    // category: "Financial",
    description: "Client management, financial planning, and compliance for financial services industry.",
    overview: "Salesforce Financial Services Cloud provides specialized CRM capabilities for financial services organizations. It offers comprehensive client management, financial planning tools, and regulatory compliance features to build stronger client relationships.",
    features: ["Client Management", "Financial Planning", "Compliance Management", "Wealth Management", "Risk Assessment", "Relationship Mapping"],
    technologies: ["Financial Services Cloud", "Client Management", "Wealth Management", "Compliance", "Risk Management", "Financial Planning"],
    benefits: ["Stronger Client Relationships", "Regulatory Compliance", "Better Financial Planning", "Risk Management", "Operational Efficiency", "Growth Opportunities"],
    process: ["Financial Strategy", "Client Onboarding", "Compliance Setup", "Risk Assessment", "Planning Tools", "Advisor Training"],
    implementation: {
      strategy: ["Financial Strategy", "Client Experience Design", "Compliance Framework", "Risk Management", "Advisor Enablement"],
      configuration: ["Client Records", "Financial Products", "Compliance Rules", "Risk Models", "Planning Tools"],
      optimization: ["Client Engagement", "Compliance Monitoring", "Risk Optimization", "Advisor Productivity", "Business Growth"]
    },
    clouds: ["Financial Services Cloud", "Sales Cloud", "Service Cloud", "Marketing Cloud", "Analytics Cloud", "Einstein Platform"]
  },
  "transportation": {
    // icon: Truck,
    title: "Transportation & Logistics",
    // category: "Logistics",
    description: "Shipment tracking, route optimization, and carrier management for transportation industry.",
    overview: "Salesforce Transportation & Logistics solutions provide comprehensive supply chain visibility, shipment tracking, and carrier management capabilities. It helps logistics companies optimize operations and deliver exceptional customer service.",
    features: ["Shipment Tracking", "Route Optimization", "Carrier Management", "Logistics Planning", "Supply Chain Visibility", "Customer Communications"],
    technologies: ["Transportation Management", "Logistics Cloud", "Route Planning", "Carrier Portal", "IoT Integration", "Real-time Tracking"],
    benefits: ["Operational Efficiency", "Cost Reduction", "Better Visibility", "Customer Satisfaction", "Route Optimization", "Carrier Relationships"],
    process: ["Logistics Strategy", "Carrier Onboarding", "Route Planning", "Tracking Setup", "Customer Portal", "Performance Monitoring"],
    implementation: {
      strategy: ["Transportation Strategy", "Carrier Network Design", "Route Optimization", "Customer Experience", "Technology Integration"],
      configuration: ["Carrier Setup", "Route Planning", "Tracking Systems", "Customer Portal", "Performance Dashboards"],
      optimization: ["Route Optimization", "Carrier Performance", "Cost Management", "Customer Experience", "Operational Excellence"]
    },
    clouds: ["Transportation Cloud", "Service Cloud", "IoT Cloud", "Analytics Cloud", "Community Cloud", "Mobile"]
  },
  "field-service": {
    // icon: Settings,
    title: "Field Service",
    // category: "Field Service",
    description: "Work order management, technician scheduling, and mobile access for field service operations.",
    overview: "Salesforce Field Service Lightning provides comprehensive field service management capabilities including intelligent scheduling, mobile workforce management, and IoT integration to optimize field operations and improve customer satisfaction.",
    features: ["Work Order Management", "Technician Scheduling", "Mobile Access", "Inventory Management", "Asset Management", "Customer Communications"],
    technologies: ["Field Service Lightning", "Mobile App", "Scheduling Optimization", "Resource Management", "IoT Integration", "Einstein Analytics"],
    benefits: ["Improved Efficiency", "Better Scheduling", "Enhanced Customer Service", "Mobile Productivity", "Asset Optimization", "Cost Reduction"],
    process: ["Field Service Strategy", "Resource Planning", "Scheduling Setup", "Mobile Configuration", "Asset Management", "Technician Training"],
    implementation: {
      strategy: ["Field Service Strategy", "Resource Optimization", "Scheduling Framework", "Mobile Strategy", "Asset Management"],
      configuration: ["Work Order Setup", "Scheduling Rules", "Mobile Configuration", "Inventory Setup", "Asset Tracking"],
      optimization: ["Scheduling Optimization", "Mobile Performance", "Asset Utilization", "Customer Experience", "Operational Excellence"]
    },
    clouds: ["Field Service Lightning", "Service Cloud", "IoT Cloud", "Mobile", "Analytics Cloud", "CPQ"]
  },
  "experience-cloud": {
    // icon: Award,
    title: "Experience Cloud",
    // category: "Experience",
    description: "Digital experiences, portal creation, and content management for customer experiences.",
    overview: "Salesforce Experience Cloud enables organizations to create branded digital experiences for customers, partners, and employees. It provides flexible portal creation, content management, and community building capabilities.",
    features: ["Digital Experiences", "Portal Creation", "Content Management", "Self-Service Portals", "Community Building", "Brand Customization"],
    technologies: ["Experience Cloud", "Community Builder", "CMS", "Portal Templates", "Lightning Components", "Personalization"],
    benefits: ["Enhanced User Experience", "Self-Service Capabilities", "Brand Consistency", "Community Engagement", "Operational Efficiency", "Customer Satisfaction"],
    process: ["Experience Strategy", "Portal Design", "Content Creation", "User Management", "Launch & Training", "Community Management"],
    implementation: {
      strategy: ["Experience Strategy", "User Journey Design", "Content Strategy", "Community Strategy", "Brand Guidelines"],
      configuration: ["Portal Setup", "Content Management", "User Permissions", "Brand Customization", "Integration Setup"],
      optimization: ["User Experience", "Content Optimization", "Community Growth", "Performance Monitoring", "Engagement Analytics"]
    },
    clouds: ["Experience Cloud", "Community Cloud", "Service Cloud", "Marketing Cloud", "Commerce Cloud", "Analytics Cloud"]
  },
  "tableau-crm": {
    // icon: Layers,
    title: "Tableau CRM",
    // category: "Advanced Analytics",
    description: "Advanced analytics, AI-powered insights, and embedded analytics for business intelligence.",
    overview: "Tableau CRM provides advanced analytics and AI-powered insights directly within the Salesforce platform. It enables organizations to discover hidden patterns, predict outcomes, and make data-driven decisions with embedded analytics.",
    features: ["Advanced Analytics", "AI-powered Insights", "Embedded Analytics", "Predictive Modeling", "Real-time Dashboards", "Self-Service Analytics"],
    technologies: ["Tableau CRM", "Einstein Discovery", "Analytics APIs", "Embedded Dashboards", "Predictive AI", "Machine Learning"],
    benefits: ["Advanced Insights", "Predictive Intelligence", "Embedded Analytics", "Self-Service BI", "Faster Decisions", "Competitive Advantage"],
    process: ["Analytics Strategy", "Data Preparation", "Model Building", "Dashboard Creation", "User Training", "Adoption & Optimization"],
    implementation: {
      strategy: ["Analytics Strategy", "AI Strategy", "Data Architecture", "User Adoption", "Governance Framework"],
      configuration: ["Data Integration", "Model Setup", "Dashboard Creation", "Embedding Configuration", "Security Setup"],
      optimization: ["Model Optimization", "Performance Tuning", "User Experience", "Advanced Features", "Continuous Learning"]
    },
    clouds: ["Tableau CRM", "Einstein Platform", "Analytics Cloud", "Sales Cloud", "Service Cloud", "Marketing Cloud"]
  },
  "payments": {
    // icon: CreditCard,
    title: "Payments",
    // category: "Payments",
    description: "Payment processing, transaction management, and financial operations for commerce solutions.",
    overview: "Salesforce Payments provides native payment processing capabilities within the Salesforce ecosystem. It enables seamless payment collection, transaction management, and financial operations for businesses of all sizes.",
    features: ["Payment Processing", "Transaction Management", "Fraud Detection", "Recurring Billing", "Multi-currency Support", "Financial Reporting"],
    technologies: ["Salesforce Payments", "Payment Gateway", "Transaction APIs", "Billing Management", "Fraud Protection", "Financial APIs"],
    benefits: ["Seamless Integration", "Reduced Complexity", "Better Security", "Lower Costs", "Improved Cash Flow", "Enhanced Customer Experience"],
    process: ["Payment Strategy", "Gateway Setup", "Integration Configuration", "Security Implementation", "Testing", "Go-Live Support"],
    implementation: {
      strategy: ["Payment Strategy", "Integration Design", "Security Framework", "Compliance Planning", "Customer Experience"],
      configuration: ["Payment Gateway", "Transaction Processing", "Security Setup", "Billing Configuration", "Reporting Setup"],
      optimization: ["Transaction Optimization", "Security Enhancement", "Performance Monitoring", "Cost Optimization", "Customer Experience"]
    },
    clouds: ["Payments", "Commerce Cloud", "CPQ", "Revenue Cloud", "Sales Cloud", "Service Cloud"]
  },
  "work-com": {
    // icon: UserCheck,
    title: "Work.com",
    // category: "Workplace",
    description: "Employee wellness, workplace safety, and return-to-work solutions for workplace management.",
    overview: "Salesforce Work.com provides comprehensive workplace management solutions including employee health screening, contact tracing, and return-to-work planning. It helps organizations maintain safe and healthy work environments.",
    features: ["Employee Wellness", "Workplace Safety", "Return-to-Work", "Health Screening", "Contact Tracing", "Compliance Monitoring"],
    technologies: ["Work.com", "Workplace Command Center", "Health Check", "Contact Tracing", "Mobile Health", "Compliance Tools"],
    benefits: ["Employee Safety", "Compliance Assurance", "Operational Continuity", "Risk Mitigation", "Health Monitoring", "Workplace Confidence"],
    process: ["Workplace Strategy", "Health Protocols", "Screening Setup", "Contact Tracing", "Compliance Configuration", "Employee Training"],
    implementation: {
      strategy: ["Workplace Strategy", "Health & Safety Framework", "Compliance Requirements", "Communication Plan", "Change Management"],
      configuration: ["Health Screening", "Contact Tracing", "Compliance Monitoring", "Mobile Setup", "Reporting Dashboards"],
      optimization: ["Process Optimization", "Health Monitoring", "Compliance Enhancement", "Employee Experience", "Risk Management"]
    },
    clouds: ["Work.com", "Service Cloud", "Community Cloud", "Mobile", "Analytics Cloud", "Einstein Platform"]
  },
  "nonprofit": {
    // icon: Briefcase,
    title: "Nonprofit Cloud",
    // category: "Nonprofit",
    description: "Donor management, program tracking, and volunteer coordination for nonprofit organizations.",
    overview: "Salesforce Nonprofit Cloud provides specialized tools for nonprofit organizations including donor management, program tracking, volunteer coordination, and outcome measurement to help nonprofits achieve their mission more effectively.",
    features: ["Donor Management", "Program Tracking", "Volunteer Coordination", "Grant Management", "Fundraising Campaigns", "Impact Measurement"],
    technologies: ["Nonprofit Cloud", "NPSP", "Program Management Module", "Volunteer Workforce", "Grant Management", "Fundraising"],
    benefits: ["Better Donor Relationships", "Program Effectiveness", "Volunteer Engagement", "Grant Success", "Operational Efficiency", "Mission Impact"],
    process: ["Nonprofit Strategy", "Donor Segmentation", "Program Setup", "Volunteer Management", "Grant Tracking", "Impact Measurement"],
    implementation: {
      strategy: ["Nonprofit Strategy", "Donor Engagement", "Program Framework", "Volunteer Strategy", "Impact Measurement"],
      configuration: ["Donor Database", "Program Setup", "Volunteer Portal", "Grant Tracking", "Fundraising Campaigns"],
      optimization: ["Donor Engagement", "Program Optimization", "Volunteer Retention", "Grant Success", "Mission Impact"]
    },
    clouds: ["Nonprofit Cloud", "Marketing Cloud", "Community Cloud", "Analytics Cloud", "Service Cloud", "Experience Cloud"]
  },
  "digital-engagement": {
    // icon: Monitor,
    title: "Digital Engagement",
    // category: "Engagement",
    description: "Digital messaging, chat support, and omnichannel engagement for customer communication.",
    overview: "Salesforce Digital Engagement provides omnichannel communication capabilities including digital messaging, video support, and social customer service. It enables businesses to engage customers across all digital channels seamlessly.",
    features: ["Digital Messaging", "Chat Support", "Video Support", "Social Customer Service", "Omnichannel Routing", "AI-Powered Assistance"],
    technologies: ["Digital Engagement", "Live Message", "Video Support", "Social Studio", "Einstein Bots", "Omnichannel"],
    benefits: ["Enhanced Customer Experience", "Omnichannel Consistency", "Improved Response Times", "Agent Productivity", "Customer Satisfaction", "Cost Efficiency"],
    process: ["Engagement Strategy", "Channel Setup", "Agent Training", "AI Configuration", "Integration Setup", "Performance Monitoring"],
    implementation: {
      strategy: ["Digital Strategy", "Channel Strategy", "Customer Experience Design", "AI Strategy", "Agent Enablement"],
      configuration: ["Channel Setup", "Routing Rules", "AI Configuration", "Agent Console", "Integration Setup"],
      optimization: ["Channel Optimization", "AI Enhancement", "Agent Performance", "Customer Experience", "Operational Excellence"]
    },
    clouds: ["Digital Engagement", "Service Cloud", "Marketing Cloud", "Social Studio", "Einstein Platform", "Community Cloud"]
  },
  "devops": {
    // icon: GitBranch,
    title: "DevOps Center",
    // category: "DevOps",
    description: "Change management, deployment automation, and release management for development operations.",
    overview: "Salesforce DevOps Center provides comprehensive development lifecycle management including source control, deployment automation, and release management. It enables development teams to deliver high-quality applications faster and more reliably.",
    features: ["Change Management", "Deployment Automation", "Release Management", "Source Control", "CI/CD Pipelines", "Quality Gates"],
    technologies: ["DevOps Center", "Change Sets", "Deployment APIs", "Metadata API", "Source Control", "Automation Tools"],
    benefits: ["Faster Deployments", "Better Quality", "Reduced Risk", "Team Collaboration", "Process Standardization", "Compliance Assurance"],
    process: ["DevOps Strategy", "Source Control Setup", "Pipeline Configuration", "Quality Gates", "Release Planning", "Team Training"],
    implementation: {
      strategy: ["DevOps Strategy", "Branching Strategy", "Deployment Framework", "Quality Framework", "Team Structure"],
      configuration: ["Source Control", "CI/CD Pipelines", "Quality Gates", "Deployment Rules", "Monitoring Setup"],
      optimization: ["Pipeline Optimization", "Quality Enhancement", "Performance Monitoring", "Team Productivity", "Risk Mitigation"]
    },
    clouds: ["DevOps Center", "Platform", "Heroku", "MuleSoft", "Analytics Cloud", "Security"]
  },
  "testing": {
    // icon: TestTube,
    title: "Testing & Quality",
    // category: "Quality",
    description: "Automated testing, quality assurance, and performance monitoring for application quality.",
    overview: "Salesforce Testing & Quality solutions provide comprehensive testing capabilities including automated testing, quality assurance, and performance monitoring to ensure high-quality application delivery and optimal user experience.",
    features: ["Automated Testing", "Quality Assurance", "Performance Monitoring", "Test Data Management", "Load Testing", "Security Testing"],
    technologies: ["Salesforce Testing", "Test Automation", "Performance Tools", "Quality Gates", "Test Data", "Security Scanning"],
    benefits: ["Higher Quality", "Faster Testing", "Reduced Bugs", "Better Performance", "Risk Mitigation", "Customer Satisfaction"],
    process: ["Testing Strategy", "Test Planning", "Automation Setup", "Performance Testing", "Quality Gates", "Continuous Monitoring"],
    implementation: {
      strategy: ["Testing Strategy", "Quality Framework", "Automation Strategy", "Performance Standards", "Security Requirements"],
      configuration: ["Test Automation", "Performance Tools", "Quality Gates", "Test Data", "Monitoring Setup"],
      optimization: ["Test Optimization", "Performance Tuning", "Quality Enhancement", "Automation Expansion", "Continuous Improvement"]
    },
    clouds: ["Testing Platform", "DevOps Center", "Platform", "Security", "Performance", "Quality"]
  },
  "innovation": {
    // icon: Rocket,
    title: "Innovation Platform",
    // category: "Innovation",
    description: "AI/ML capabilities, emerging technologies, and innovation tools for future-ready solutions.",
    overview: "Salesforce Innovation Platform provides access to cutting-edge technologies including AI/ML capabilities, emerging technologies, and innovation tools. It enables organizations to experiment with and adopt future-ready solutions.",
    features: ["AI/ML Capabilities", "Emerging Technologies", "Innovation Labs", "Experimental Features", "Research Tools", "Future Technologies"],
    technologies: ["Einstein Platform", "Trailhead", "Innovation Zones", "Labs Features", "Research APIs", "Experimental Tools"],
    benefits: ["Future-Ready Solutions", "Competitive Advantage", "Innovation Culture", "Technology Leadership", "Rapid Experimentation", "Digital Transformation"],
    process: ["Innovation Strategy", "Technology Evaluation", "Pilot Projects", "Experimentation", "Scaling Success", "Culture Change"],
    implementation: {
      strategy: ["Innovation Strategy", "Technology Roadmap", "Experimentation Framework", "Culture Strategy", "Success Metrics"],
      configuration: ["Innovation Tools", "Experimental Features", "Research Access", "Pilot Environment", "Learning Resources"],
      optimization: ["Innovation Acceleration", "Technology Adoption", "Experimentation Scale", "Culture Enhancement", "Future Planning"]
    },
    clouds: ["Einstein Platform", "Innovation Labs", "Research", "Experimental", "Future Tech", "AI/ML"]
  },
  "hyperforce": {
    // icon: Network,
    title: "Hyperforce",
    // category: "Infrastructure",
    description: "Cloud infrastructure, scalability, and global deployment for enterprise-grade hosting.",
    overview: "Salesforce Hyperforce provides next-generation cloud infrastructure with enhanced scalability, security, and global deployment capabilities. It enables organizations to leverage enterprise-grade hosting with improved performance and compliance.",
    features: ["Cloud Infrastructure", "Global Scalability", "Data Residency", "Enterprise Security", "High Availability", "Performance Optimization"],
    technologies: ["Hyperforce", "Multi-Cloud", "Global Infrastructure", "Security Controls", "Performance Optimization", "Compliance Tools"],
    benefits: ["Enhanced Performance", "Global Scale", "Better Security", "Compliance Assurance", "Cost Efficiency", "Future-Proof Infrastructure"],
    process: ["Infrastructure Assessment", "Migration Planning", "Security Configuration", "Performance Optimization", "Compliance Verification", "Go-Live Support"],
    implementation: {
      strategy: ["Infrastructure Strategy", "Migration Strategy", "Security Framework", "Compliance Planning", "Performance Standards"],
      configuration: ["Infrastructure Setup", "Security Configuration", "Performance Tuning", "Compliance Settings", "Monitoring Setup"],
      optimization: ["Performance Optimization", "Security Enhancement", "Cost Optimization", "Compliance Monitoring", "Infrastructure Evolution"]
    },
    clouds: ["Hyperforce", "Security", "Compliance", "Performance", "Global Infrastructure", "Multi-Cloud"]
  }
};

const SalesforceDetail = () => {
  const { moduleId } = useParams<{ moduleId: string }>();
  
  // Force re-render when moduleId changes
  useEffect(() => {
    // Scroll to top immediately and after a short delay to ensure it works
    window.scrollTo(0, 0);
    const timeoutId = setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [moduleId]);
  
  if (!moduleId || !salesforceModuleData[moduleId]) {
    return <Navigate to="/404" replace />;
  }

  const module = salesforceModuleData[moduleId];
  // const IconComponent = module.icon;

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={salesforceBg} 
            alt="Salesforce CRM Solutions"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-hero opacity-90"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          {/* <Button 
            variant="outline" 
            className="mb-8 border-white text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm"
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Salesforce Modules
          </Button> */}
          
          <div className="max-w-4xl text-white">
            {/* <div className="flex items-center gap-4 mb-6">
              <div className="bg-gradient-primary p-4 rounded-xl shadow-glow">
                <IconComponent className="h-8 w-8 text-white" />
              </div>
              <Badge variant="secondary" className="bg-white/10 text-white border-white/20">
                {module.category}
              </Badge>
            </div> */}
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white mt-12">
              {module.title}
            </h1>
            
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              {module.description}
            </p>
            
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold mb-6 text-foreground">
                Cloud Overview
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-8">
                {module.overview}
              </p>
              
              <h3 className="text-xl font-semibold mb-4 text-foreground">
                Key Benefits
              </h3>
              <div className="grid gap-3">
                {module.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                    <span className="text-muted-foreground">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <Card className="bg-gradient-card border-0 shadow-elegant">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-primary" />
                  Core Features
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3">
                  {module.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-background/50 rounded-lg">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span className="text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-16 bg-accent/5">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-foreground">
            Technologies & Tools
          </h2>
          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
            {module.technologies.map((tech, index) => (
              <Badge key={index} variant="outline" className="px-4 py-2 text-sm">
                {tech}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-foreground">
            Implementation Approach
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-gradient-card border-0 shadow-elegant">
              <CardHeader>
                <CardTitle className="text-xl font-semibold text-foreground">Strategy Phase</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {module.implementation.strategy.map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-muted-foreground text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-card border-0 shadow-elegant">
              <CardHeader>
                <CardTitle className="text-xl font-semibold text-foreground">Configuration</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {module.implementation.configuration.map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-muted-foreground text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-card border-0 shadow-elegant">
              <CardHeader>
                <CardTitle className="text-xl font-semibold text-foreground">Optimization</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {module.implementation.optimization.map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-warning rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-muted-foreground text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Related Clouds */}
      <section className="py-16 bg-accent/5">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-foreground">
            Related Salesforce Clouds
          </h2>
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {module.clouds.map((cloud, index) => (
              <div key={index} className="bg-gradient-card px-4 py-2 rounded-full border border-primary/20">
                <span className="text-sm font-medium text-foreground">{cloud}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SalesforceDetail;